import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { CartProvider } from "@/lib/cart";
import { AuthProvider } from "@/lib/auth";
import { CustomerAuthProvider } from "@/lib/customerAuth";
import { BactPromptProvider } from "@/lib/bactPrompt";
import { SiteSettingsProvider } from "@/lib/site-settings";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import PendingCardWatcher from "@/components/PendingCardWatcher";
import EmailCaptureBar from "@/components/EmailCaptureBar";
import ReferralCapture from "@/components/ReferralCapture";
import AttributionCapture from "@/components/AttributionCapture";
import OrganicTracker from "@/components/OrganicTracker";
import CookieConsent from "@/components/CookieConsent";
import AgeGate from "@/components/AgeGate";
import AdLanding from "@/pages/AdLanding";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import CurrentlyAvailable from "@/pages/CurrentlyAvailable";
import Quality from "@/pages/Quality";
import SameDayDispatch from "@/pages/SameDayDispatch";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import FenaConfirm from "@/pages/FenaConfirm";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Legal from "@/pages/Legal";
import HelpPayByBank from "@/pages/HelpPayByBank";
import UkResearchPeptideSupplier from "@/pages/UkResearchPeptideSupplier";
import Faq from "@/pages/Faq";
import GuideWhereToBuy from "@/pages/GuideWhereToBuy";
import GuideReadCoa from "@/pages/GuideReadCoa";
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerDashboard from "@/pages/PartnerDashboard";

import AccountLogin from "@/pages/AccountLogin";
import { ForgotPassword, ResetPassword } from "@/pages/AccountPasswordReset";
import Account from "@/pages/Account";
import Affiliates from "@/pages/Affiliates";

import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProducts from "@/pages/AdminProducts";
import AdminOrders from "@/pages/AdminOrders";
import AdminCampaigns from "@/pages/AdminCampaigns";
import AdminNotify from "@/pages/AdminNotify";
import AdminContact from "@/pages/AdminContact";
import AdminDiscounts from "@/pages/AdminDiscounts";
import AdminAffiliates from "@/pages/AdminAffiliates";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminDeliverability from "@/pages/AdminDeliverability";
import AdminSocial from "@/pages/AdminSocial";
import AdminEmailActivity from "@/pages/AdminEmailActivity";
import AdminAdsLaunch from "@/pages/AdminAdsLaunch";
import AdminSettings from "@/pages/AdminSettings";
import AdminCustomers from "@/pages/AdminCustomers";
import AdminDispatch from "@/pages/AdminDispatch";
import AdminAttribution from "@/pages/AdminAttribution";
import AdminSalesReport from "@/pages/AdminSalesReport";
import AdminSeo from "@/pages/AdminSeo";
import WhatsAppButton from "@/components/WhatsAppButton";

const Public = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CustomerAuthProvider>
          <CartProvider>
            <SiteSettingsProvider>
            <BrowserRouter>
              <BactPromptProvider>
                <ScrollToTop />
                <PendingCardWatcher />
                <ReferralCapture />
                <AttributionCapture />
                <OrganicTracker />
                <EmailCaptureBar />
                <CookieConsent />
                <AgeGate />
                <WhatsAppButton />
                <Toaster
                  theme="dark"
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: "#111A2B",
                      border: "1px solid rgba(0,194,214,0.3)",
                      color: "#F7FAFC",
                      fontFamily: "'Inter', sans-serif",
                    },
                  }}
                />
                <Routes>
                  <Route path="/" element={<Public><Home /></Public>} />
                  <Route path="/products" element={<Public><Products /></Public>} />
                  <Route path="/products/:sku" element={<Public><ProductDetail /></Public>} />
                  {/* /shop is a marketing-email alias for /products — keeps UTM params intact */}
                  <Route path="/shop" element={<Navigate to={`/products${window.location.search}`} replace />} />
                  <Route path="/currently-available" element={<Public><CurrentlyAvailable /></Public>} />
                  <Route path="/quality" element={<Public><Quality /></Public>} />
                  <Route path="/same-day-dispatch" element={<Public><SameDayDispatch /></Public>} />
                  <Route path="/about" element={<Public><About /></Public>} />
                  <Route path="/contact" element={<Public><Contact /></Public>} />
                  <Route path="/cart" element={<Public><Cart /></Public>} />
                  <Route path="/checkout" element={<Public><Checkout /></Public>} />
                  <Route path="/checkout/fena-confirm" element={<Public><FenaConfirm /></Public>} />
                  <Route path="/checkout/secure-payment" element={<Public><FenaConfirm /></Public>} />
                  <Route path="/order/:orderNumber" element={<Public><OrderConfirmation /></Public>} />
                  <Route path="/uk-research-peptide-supplier" element={<Public><UkResearchPeptideSupplier /></Public>} />
                  <Route path="/partner/login" element={<Public><PartnerLogin /></Public>} />
                  <Route path="/partner/dashboard" element={<Public><PartnerDashboard /></Public>} />
                  <Route path="/legal/:slug" element={<Public><Legal /></Public>} />
                  <Route path="/help/pay-by-bank" element={<Public><HelpPayByBank /></Public>} />
                  <Route path="/faq" element={<Public><Faq /></Public>} />
                  <Route path="/guides/where-to-buy-peptides-uk" element={<Public><GuideWhereToBuy /></Public>} />
                  <Route path="/guides/how-to-read-a-peptide-coa" element={<Public><GuideReadCoa /></Public>} />

                  {/* Brand-safe paid-ad landing pages (no Layout = sterile, no email-capture overlays) */}
                  <Route path="/lp/research-supplier-uk" element={<AdLanding variant="research-supplier" />} />
                  <Route path="/lp/lab-peptides-uk" element={<AdLanding variant="lab-peptides" />} />

                  {/* Affiliate programme (public) */}
                  <Route path="/affiliates" element={<Public><Affiliates /></Public>} />
                  <Route path="/affiliates/dashboard" element={<Public><Affiliates /></Public>} />

                  {/* Customer account */}
                  <Route path="/account" element={<Account />} />
                  <Route path="/account/login" element={<AccountLogin />} />
                  <Route path="/account/forgot" element={<ForgotPassword />} />
                  <Route path="/account/reset" element={<ResetPassword />} />

                  {/* Hidden staff portal (admin) */}
                  <Route path="/staff-portal-72f4" element={<AdminLogin />} />
                  <Route path="/admin/login" element={<Navigate to="/account/login" replace />} />
                  <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="dispatch" element={<AdminDispatch />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="notify" element={<AdminNotify />} />
                    <Route path="contact" element={<AdminContact />} />
                    <Route path="discounts" element={<AdminDiscounts />} />
                    <Route path="campaigns" element={<AdminCampaigns />} />
                    <Route path="attribution" element={<AdminAttribution />} />
                    <Route path="sales-report" element={<AdminSalesReport />} />
                    <Route path="seo" element={<AdminSeo />} />
                    <Route path="affiliates" element={<AdminAffiliates />} />
                    <Route path="payouts" element={<AdminPayouts />} />
                    <Route path="social" element={<AdminSocial />} />
                    <Route path="email-activity" element={<AdminEmailActivity />} />
                    <Route path="launch-ads" element={<AdminAdsLaunch />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="deliverability" element={<AdminDeliverability />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                  </Route>
                </Routes>
              </BactPromptProvider>
            </BrowserRouter>
            </SiteSettingsProvider>
          </CartProvider>
        </CustomerAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
